import { components } from "react-select";
import {
  INVENTORY_MODAL_TYPES,
  EMPTY_STRING,
  globalConstants,
  INITIAL_VALUE_TYPES,
  inventoryConstants,
  preserveMode as preserveModeConstants,
  invoiceConstants,
  toastMessages,
  toastType,
  transactionConstants,
  PRODUCT_JSON_TYPE,
} from "../../constants";
import {
  PRODUCT_TYPES_ENUMS,
  PRODUCT_CONDITIONS_ENUMS,
  TCG_PLAYER_API_GRADE_ENUMS,
} from "../../system/globalEnums";
import { SearchIcon } from "../../assets/icons/iconsProvider";
import {
  convertToFixedPrecision,
  convertToUsd,
  customToast,
  getProductSubCategory,
  getTotalCogsOfBatchProducts,
  parseToDecimalNumber,
  parseToNumber,
  getTypeOrCategoryObject,
  isTagsEqual,
  parseToThousandSeperatorDecimalNumber,
  getTypeOrCategoryIdByName,
  handleIsInvalidInteger,
  onChangeStockPrice,
  downloadCSV,
  beautifyStringForImport,
  exportStringArrayFunc,
  exportUpcFunc,
  exportTagFunc,
  isSpinnerEnabled,
  onChangeProductCondition,
} from "../utility";
import ItemImage from "../../assets/images/productimage.png";

export const isTcgPlayerApi = (product) => {
  return (
    product?.apiSource === inventoryConstants.TCG_PLAYER_API &&
    product?.productType === PRODUCT_TYPES_ENUMS.TRADING_CARD
  );
};

//-------check is product trading card
export const isTradingCard = (product) => {
  return product.productType === PRODUCT_TYPES_ENUMS.TRADING_CARD;
};

//-------check is product Video Game
export const isVideoGame = (product) => !isTradingCard(product);

//-------check is store custom product
export const isStoreCustomProduct = (product, key = "sourceApi") =>
  product[key] === inventoryConstants.STORE_CUSTOM_PRODUCT;

export const isCustomProduct = (inv) => {
  if (inv?.tags?.length === 0 || !inv?.tags) {
    return false;
  }
  return inv.tags?.includes(inventoryConstants.CUSTOM_ITEM_TAG);
};

//------custom input for react select
export const CustomInput = (props) => (
  <div>
    <components.Input {...props} />
    <SearchIcon className="dropdown-icon" />
  </div>
);

// returns the quantity property (inStockQuantity or onlineQuantity) based on isSeprateQuantity as in add product flows we can only add one at a time
export const getQuantityPropertyToUpdate = (product) => {
  let propertyName = EMPTY_STRING;
  if (product.isSeparateQuantity) {
    propertyName = "onlineQuantity";
  } else {
    propertyName = "inStockQuantity";
  }
  return propertyName;
};

// returns the quantity value (inStockQuantity or onlineQuantity) based on isSeparateQuantity as in add product flows we can only add one at a time
export const getQuantityValue = (
  product,
  isDbJson = false,
  isSum = false,
  shouldUseIsSeparateQuantityInDBJson = false,
  shouldUseIsSeparateQuantityFromPrice = false
) => {
  let value = 0;

  if (product) {
    let quantity = 0;
    let onlineQuantity = 0;

    if (isDbJson) {
      quantity = Number(product.price?.quantity || 0);
      onlineQuantity = Number(product.price?.onlineQuantity || 0);

      if (isSum) {
        value = quantity + onlineQuantity;
      } else if (shouldUseIsSeparateQuantityInDBJson) {
        value = product?.isSeparateQuantity ? onlineQuantity : quantity;
      } else if (shouldUseIsSeparateQuantityFromPrice) {
        value = product?.price?.isSeparateQuantity ? onlineQuantity : quantity;
      } else {
        value = product?.price?.isOnlineQuantity ? onlineQuantity : quantity;
      }
    } else {
      quantity = Number(product.inStockQuantity || 0);
      onlineQuantity = Number(product.onlineQuantity || 0);

      if (isSum) {
        value = quantity + onlineQuantity;
      } else {
        value = product.isSeparateQuantity ? onlineQuantity : quantity;
      }
    }
  }
  return Number(value);
};

//--------Add Batch Inventory On Change Product Quantity
export const onChangeProductQuantity = (
  Id,
  newQuantity,
  addBatchInventory,
  setAddBatchInventory,
  handlePercentageFlag
) => {
  newQuantity = Number(newQuantity);

  // Find the item to update
  const itemToUpdate = addBatchInventory.find((item) => item.id === Id);

  if (itemToUpdate) {
    // Update the quantity directly
    itemToUpdate[getQuantityPropertyToUpdate(itemToUpdate)] = newQuantity;

    // Create a new array to trigger React's re-render
    setAddBatchInventory(handlePercentageFlag([...addBatchInventory]));
  }
};

export const handleValueUpdateInBatchModalRow = (
  row,
  columnNameArr,
  valueArr,
  addBatchInventory,
  setAddBatchInventory = () => {},
  storeId = EMPTY_STRING, // In trade case batch is saved against store id
  markupPresets,
  tradeinMargin,
  isTradeBatch
) => {
  let tempBatchRow = {};
  // Apply changes in batch array
  let updatedBatchInventory = addBatchInventory.map((batchRow) => {
    tempBatchRow = batchRow;
    if (batchRow.id === row.id && columnNameArr.length === valueArr.length) {
      tempBatchRow = {
        ...batchRow,
      };
      columnNameArr.forEach((name, index) => {
        tempBatchRow[name] = valueArr[index];
      });
    }
    return tempBatchRow;
  });
  // On grade update condition, condition list, market price, cogs (cash offer, trade offer) is also recalculated
  if (columnNameArr.includes("grade")) {
    updatedBatchInventory = onChangeProductCondition(
      markupPresets,
      row.id,
      null,
      isTradeBatch,
      updatedBatchInventory,
      () => {},
      tradeinMargin
    );
  }
  setAddBatchInventory(updatedBatchInventory, storeId);
  return updatedBatchInventory;
};

export const getCogsValue = (
  product,
  isDbJson = false,
  shouldMultiplyByQuantity = false,
  useIsSeparateQuantityInDBJson = false
) => {
  let value = EMPTY_STRING;
  // isDbJson get quantity from item.price instead of item
  // shouldMultiplyByQuantity is used when we want to show total cogs for each product (cogs * quantity)
  if (isDbJson) {
    const priceObject = product?.price || {};
    if (useIsSeparateQuantityInDBJson) {
      value = priceObject?.isSeparateQuantity
        ? priceObject?.onlineUnitPurchasePrice || 0
        : priceObject?.unit_purchase_price || 0;
    } else {
      value = priceObject?.isOnlineQuantity
        ? priceObject?.onlineUnitPurchasePrice || 0
        : priceObject?.unit_purchase_price || 0;
    }
  } else {
    value = product?.isSeparateQuantity
      ? product?.onlineUnitPurchasePrice || 0
      : product?.costOfGoods || 0;
  }

  if (!shouldMultiplyByQuantity) return value;

  const quantity = getQuantityValue(product, isDbJson, isDbJson);

  return value * quantity;
};
export const getCogsProperty = (product, isDbJson = true) => {
  if (isDbJson) {
    const priceObject = product?.price || {};
    return priceObject?.isSeparateQuantity
      ? "onlineUnitPurchasePrice"
      : "unit_purchase_price";
  }
  return product?.isSeparateQuantity
    ? "onlineUnitPurchasePrice"
    : "costOfGoods";
};

export const getStockPrice = (product, isDbJson = true, isValue = false) => {
  let response = EMPTY_STRING;
  let propertyName = EMPTY_STRING;
  if (isDbJson) {
    const priceObject = product?.price || {};
    propertyName = priceObject?.isSeparateQuantity
      ? "onlineUnitSellPrice"
      : "unit_sell_price";
  } else {
    propertyName = product.isSeparateQuantity
      ? "onlineUnitSellPrice"
      : "inStockPrice";
  }

  if (isValue) {
    if (isDbJson) {
      const priceObject = product?.price || {};
      response = Number(priceObject[propertyName] || 0);
    } else {
      response = Number(product[propertyName] || 0);
    }
  } else {
    response = propertyName;
  }

  return response;
};

export const batchInventorySubmitValidation = (
  batchInventory,
  isCompareTradeValues = false
) => {
  let isValid = false;
  return batchInventory.some((inv) => {
    isValid =
      (Number(inv.inStockQuantity) === 0 && Number(inv.onlineQuantity) === 0) ||
      (Number(inv.inStockPrice) === 0 && Number(inv.onlineSellPrice) === 0);
    if (isCompareTradeValues && isValid) {
      isValid =
        (Number(inv.cashOffer) === 0 && Number(inv.onlineCashOffer) === 0) ||
        (inv.cashOffer === "" && inv.onlineCashOffer === "") ||
        (Number(inv.tradeOffer) === 0 && Number(inv.onlineTradeOffer) === 0) ||
        (inv.tradeOffer === "" && inv.onlineTradeOffer === "");
    }
    return isValid;
  });
};

// for Calculate each product percentage in batch
export const handleCalculateEachProductPercentage = (
  percentageFlagBatchInv
) => {
  const totalCost = getTotalCogsOfBatchProducts(percentageFlagBatchInv);

  return percentageFlagBatchInv.map((item) => {
    if (!item.isItemLocked) {
      // const itemTotalCostOfGoods =
      //   isStoreCustomProduct(item, "apiSource") && !item.trackQuantity
      //     ? Number(item.costOfGoods * 1)
      //     : Number(item.costOfGoods * item.inStockQuantity);
      const itemTotalCostOfGoods = getCogsValue(item, false, true);
      const cogsPercentage =
        totalCost > 0 ? (itemTotalCostOfGoods / totalCost) * 100 : 0;

      return {
        ...item,
        cogsPercentage: convertToFixedPrecision(cogsPercentage),
        [getCogsProperty(item, false)]:
          cogsPercentage === 0
            ? inventoryConstants.DEFAULT_OFFER_VALUE
            : getCogsValue(item, false, false),
      };
    }

    return item;
  });
};

//-------handle cogs of batch product
export const batchCostOfGoodsChange = (
  Id,
  newOffer,
  addBatchInventory,
  setAddBatchInventory
) => {
  const updatedInventory = addBatchInventory.map((item) => {
    if (item.id === Id) {
      return {
        ...item,
        [getCogsProperty(item, false)]: newOffer,
      };
    }
    return item;
  });
  setAddBatchInventory(handleCalculateEachProductPercentage(updatedInventory));
};

//-------handle onchnage product stock price. Used in add batch inventory and add trade batch inventory
export const batchStockPriceChange = (
  Id,
  newPrice,
  currentStore,
  markupPresets,
  addBatchInventory,
  setAddBatchInventory,
  isCalculateCogsOnStockPriceUpdate = true
) => {
  const updatedInventory = addBatchInventory.map((item) => {
    if (item.id === Id) {
      const updatedItem = {
        ...item,
        [getStockPrice(item, false)]: newPrice,
        marketPrice: isStoreCustomProduct(item, "apiSource")
          ? newPrice
          : item.marketPrice,
      };
      const updatedPrices = onChangeStockPrice(
        newPrice,
        () => {},
        updatedItem.marketPrice,
        null,
        item?.tradeInMarginTypeObject,
        currentStore,
        undefined,
        markupPresets,
        item.productType,
        item,
        false,
        item.isSeparateQuantity
      );
      if (isCalculateCogsOnStockPriceUpdate) {
        if (item.isSeparateQuantity) {
          updatedItem.onlineUnitPurchasePrice = updatedPrices.onlineCashOffer;
          updatedItem.cashOffer = updatedPrices.onlineCashOffer;
          updatedItem.tradeOffer = updatedPrices.onlineTradeOffer;
        } else {
          updatedItem.costOfGoods = updatedPrices.cashOffer;
          updatedItem.cashOffer = updatedPrices.cashOffer;
          updatedItem.tradeOffer = updatedPrices.tradeOffer;
        }
      }
      return updatedItem;
    } else {
      return item;
    }
  });
  setAddBatchInventory(updatedInventory);
};

//--------handle delete product from batch
export const handleBatchProductDelete = (
  Id,
  addBatchInventory,
  setAddBatchInventory
) => {
  const updatedInventory = addBatchInventory.filter((item) => item.id !== Id);

  setAddBatchInventory(
    handleCalculateEachProductPercentage(
      updatedInventory?.map((item, index) => ({ ...item, id: index })) || []
    )
  );
};

//-------handle Batch Bulk Delete
export const handleBatchBulkDelete = (
  toggledClearRows,
  addBatchInventory,
  setToggleClearRows,
  setAddBatchInventory,
  rowsToUpdateCondition,
  setRowsToUpdateCondition
) => {
  const itemToDeleteId = rowsToUpdateCondition.map((item) => item.id);

  const updatedInventory = addBatchInventory.filter(
    (item) => !itemToDeleteId.includes(item.id)
  );
  setAddBatchInventory(
    handleCalculateEachProductPercentage(
      updatedInventory?.map((item, index) => ({ ...item, id: index })) || []
    )
  );
  setToggleClearRows(!toggledClearRows);
  setRowsToUpdateCondition([]);
  // Additional actions, such as showing a success message or triggering further logic
};

//-------handle bulk update condition button click
export const handleBulkUpdateCondition = (
  isBulkModal,
  setIsBulkModal,
  rowsToUpdateCondition
) => {
  const sameProductType = rowsToUpdateCondition.every((item, index, array) => {
    return index === 0 || item["productType"] === array[0]["productType"];
  });

  if (sameProductType) {
    setIsBulkModal(!isBulkModal);
  } else {
    customToast(toastMessages.SAME_PRODUCT_TYPE_REQUIRED, toastType.ERROR);
  }
};

//--------handle Update Edit product In batch
export const addToCartInBatchTrade = (
  editProduct,
  addBatchInventory,
  setEditTradeObject,
  setAddBatchInventory
) => {
  // Find the index of the item to be edited
  const index = addBatchInventory.findIndex(
    (item) => item.id === editProduct.id
  );

  if (index !== -1) {
    // Remove the item from its original position
    const updatedInventory = [
      ...addBatchInventory.slice(0, index),
      ...addBatchInventory.slice(index + 1),
    ];

    // Insert the edited product at the same index
    updatedInventory.splice(index, 0, editProduct);

    // Update the state with the updated inventory and reset other states
    setAddBatchInventory(
      handleCalculateEachProductPercentage(updatedInventory)
    );
    setEditTradeObject("");
  }
};

// Calculate the entered input value is valid (i.e greater than the  possible minimum value): Check if the entered value is less than the possible min value (apply percentage or apply dollar in both cases this function recieved the value hence this check is valid for both)
export const isCogsMinValueValidation = (
  userInput,
  batchInventory,
  isDbJson = false
) => {
  let validMinValue = batchInventory.reduce((acc, currentValue) => {
    return (
      acc +
      getQuantityValue(currentValue, isDbJson) *
        inventoryConstants.DEFAULT_OFFER_VALUE
    );
  }, 0);
  validMinValue = parseToNumber(validMinValue);
  let isError = Number(userInput) < validMinValue;
  return {
    isError: isError,
    validMinValue: validMinValue,
  };
};

//-------handle total Cogs value Change (if flat apply then it will consider both percentage or value and if not flat then the passed value is always a number as when user select x percentage this function will be passed a value = x percent of total market price and in case of value it is given a value)
export const handleTotalCogsValueChange = (
  data,
  setTotalCost,
  addBatchInventory,
  setAddBatchInventory
) => {
  //-------handle total offer value change
  const value = Number(data.value);

  // Step 1: Check if all items are locked
  const hasUnlockedItems = addBatchInventory.some((item) => !item.isItemLocked);
  if (!hasUnlockedItems) {
    customToast(toastMessages.ALL_ITEMS_LOCKED_ERROR, toastType.ERROR);
    return; // Exit early if no unlocked items
  }

  // Step 2: Validate the inventory data for 0 or empty values
  let isInvalid = addBatchInventory.some((inv) => {
    const qty = getQuantityValue(inv, false);
    const cogs = getCogsValue(inv, false, false);
    return qty === 0 || Number(cogs) === 0;
  });
  if (isInvalid) {
    customToast("Quantity & Cost of Goods Cannot be 0", toastType.ERROR);
    return; // Exit early if invalid fields are found
  }

  // Step 3: Handle the case where offerBy.value is 1 (flat rate for all products)
  if (data.offerBy.value === 1) {
    // Update all unlocked items with the flat value
    const finalUpdatedBatchInventory = addBatchInventory.map((item) => {
      if (item.isItemLocked) {
        return item; // Skip locked items
      }
      let flatOfferValue = data?.isPercentageOffer
        ? parseToDecimalNumber(value)
        : parseToDecimalNumber(
            (Number(value) / 100) * Number(item?.marketPrice || 0)
          );
      flatOfferValue =
        flatOfferValue <= 0
          ? inventoryConstants.DEFAULT_OFFER_VALUE
          : flatOfferValue;

      return {
        ...item,
        costOfGoods: flatOfferValue,
        // costOfGoods: item.isSeparateQuantity
        //   ? item.costOfGoods
        //   : flatOfferValue,
        // onlineUnitPurchasePrice: item.isSeparateQuantity
        //   ? flatOfferValue
        //   : item.onlineUnitPurchasePrice,
      };
    });

    // Step 4: Update the state with the final inventory after applying the flat rate
    setAddBatchInventory(finalUpdatedBatchInventory);
    return; // Exit after applying the flat rate to avoid further processing
  }

  // Step 5: Continue with the original process for offerBy.value other than 1
  // Recalculate the cost of goods percentages before proceeding
  const updatedBatchInventory =
    handleCalculateEachProductPercentage(addBatchInventory);
  // Calculate total cogs for locked items based on the type of offer (costOfGoods)
  const lockedTotalOffer = parseToDecimalNumber(
    updatedBatchInventory.reduce((total, item) => {
      if (item.isItemLocked) {
        // return total + Number(item.costOfGoods);
        const cogs = getCogsValue(item, false, true);
        return total + Number(cogs);
      }
      return total;
    }, 0) ?? 0
  );

  // Step 6: Check if the incoming value is less than or equal to the total offer of locked items
  if (value <= Number(lockedTotalOffer)) {
    customToast(toastMessages.VALUE_LESS_THAN_LOCKED_ITEMS, toastType.ERROR);
    return; // Exit early if the value is invalid
  }
  // Step 6.1: read comment of function isCogsMinValueValidation
  let minValueValidationRes = isCogsMinValueValidation(
    value,
    addBatchInventory
  );
  if (minValueValidationRes.isError) {
    customToast(
      `Please enter value greater than ${minValueValidationRes.validMinValue}`,
      toastType.ERROR
    );
    return;
  }

  // Step 7: Parse the input value
  const inputValue = parseToDecimalNumber(value - Number(lockedTotalOffer));
  setTotalCost(inputValue);

  // Step 8: Update the batch inventory based on cost of goods
  let finalUpdatedBatchInventory = updatedBatchInventory.map((item) => {
    // Skip locked items (no need to update their offer values)
    if (item.isItemLocked) {
      return item; // Return unchanged item for locked items
    }

    let offerValue = 0;
    // Calculate the cost of goods for the item
    offerValue =
      (inputValue * Number(item.cogsPercentage)) /
      100 /
      // Number(item.inStockQuantity);
      getQuantityValue(item, false);

    // Apply the minimum threshold for costOfGoods (if it falls below the threshold, set it to the default value)
    offerValue = parseToDecimalNumber(
      offerValue <= inventoryConstants.DEFAULT_OFFER_VALUE
        ? inventoryConstants.DEFAULT_OFFER_VALUE
        : offerValue
    );

    return {
      ...item,
      [getCogsProperty(item, false)]: parseToDecimalNumber(offerValue),
      // costOfGoods: item.isSeparateQuantity
      //   ? item.costOfGoods
      //   : parseToDecimalNumber(offerValue),
      // onlineUnitPurchasePrice: item.isSeparateQuantity
      //   ? parseToDecimalNumber(offerValue)
      //   : item.onlineUnitPurchasePrice,
    };
  });

  // Step 9: Handle the difference in the updated inventory
  const totalOffer = getTotalCogsOfBatchProducts(finalUpdatedBatchInventory);
  const difference = parseToNumber(Number(inputValue) - Number(totalOffer));

  if (difference < 0 || difference > 0) {
    let productIndex = 0;
    // Step 10: Filter out locked items completely when calculating the product index
    const nonLockedBatchInventory = finalUpdatedBatchInventory.filter(
      (item) => !item.isItemLocked
    );
    // Step 11: Handle positive or negative difference in the respective offer (costOfGoods)
    if (difference < 0) {
      productIndex = nonLockedBatchInventory.reduce(
        (maxIndex, obj, idx, array) => {
          // return obj.costOfGoods > array[maxIndex].costOfGoods ? idx : maxIndex;
          const v1 = getCogsValue(obj, false, false);
          const v2 = getCogsValue(array[maxIndex], false, false);
          return v1 > v2 ? idx : maxIndex;
        },
        0
      );
    } else {
      productIndex = nonLockedBatchInventory.reduce(
        (minIndex, obj, idx, array) => {
          // return obj.costOfGoods < array[minIndex].costOfGoods ? idx : minIndex;
          const v1 = getCogsValue(obj, false, false);
          const v2 = getCogsValue(array[minIndex], false, false);
          return v1 < v2 ? idx : minIndex;
        },
        0
      );
    }
    // Step 12: Update the offer for the selected product based on the calculated difference
    const cogsVal = getCogsValue(
      nonLockedBatchInventory[productIndex],
      false,
      false
    );
    let newOfferValue = parseToDecimalNumber(Number(cogsVal) + difference);
    // Ensure offer doesn't go below the minimum threshold (0.01)
    if (newOfferValue < inventoryConstants.DEFAULT_OFFER_VALUE) {
      let remainingDifference =
        difference - (inventoryConstants.DEFAULT_OFFER_VALUE - cogsVal);
      // if (nonLockedBatchInventory[productIndex].isSeparateQuantity) {
      //   nonLockedBatchInventory[productIndex].onlineUnitPurchasePrice =
      //     inventoryConstants.DEFAULT_OFFER_VALUE;
      // } else {
      //   nonLockedBatchInventory[productIndex].costOfGoods =
      //     inventoryConstants.DEFAULT_OFFER_VALUE;
      // }
      nonLockedBatchInventory[productIndex][
        getCogsProperty(nonLockedBatchInventory[productIndex], false)
      ] = inventoryConstants.DEFAULT_OFFER_VALUE;
      // If there's still remaining difference, redistribute it across other unlocked products
      for (let i = 0; i < nonLockedBatchInventory.length; i++) {
        if (i === productIndex) continue;

        let otherProduct = nonLockedBatchInventory[i];
        const otherCogs = getCogsValue(otherProduct, false, false);
        let potentialNewOfferValue = otherCogs - remainingDifference;

        if (potentialNewOfferValue >= inventoryConstants.DEFAULT_OFFER_VALUE) {
          // if (otherProduct.isSeparateQuantity) {
          //   otherProduct.onlineUnitPurchasePrice = parseToDecimalNumber(
          //     potentialNewOfferValue
          //   );
          // } else {
          //   otherProduct.costOfGoods = parseToDecimalNumber(
          //     potentialNewOfferValue
          //   );
          // }
          otherProduct[getCogsProperty(otherProduct, false)] =
            potentialNewOfferValue;
          remainingDifference = 0;
          break;
        } else {
          remainingDifference -=
            otherCogs - inventoryConstants.DEFAULT_OFFER_VALUE;
          // if (otherProduct.isSeparateQuantity) {
          //   otherProduct.onlineUnitPurchasePrice =
          //     inventoryConstants.DEFAULT_OFFER_VALUE;
          // } else {
          //   otherProduct.costOfGoods = inventoryConstants.DEFAULT_OFFER_VALUE;
          // }
          otherProduct[getCogsProperty(otherProduct, false)] =
            inventoryConstants.DEFAULT_OFFER_VALUE;
        }
      }
    } else {
      nonLockedBatchInventory[productIndex].costOfGoods = newOfferValue;
    }
    // Step 13: Rebuild the final updated batch inventory without modifying locked items
    finalUpdatedBatchInventory = finalUpdatedBatchInventory.map((item) => {
      if (item.isItemLocked) {
        return item; // Return the locked items unchanged
      }

      const updatedItem = nonLockedBatchInventory.find(
        (updatedItem) => updatedItem.id === item.id // Assuming each item has a unique 'id'
      );

      return updatedItem ? updatedItem : item; // Replace the unlocked item with the updated one
    });
  }
  // Step 14: Update the inventory list state with the final values
  setAddBatchInventory(finalUpdatedBatchInventory);
};

//-------Prepare Filter For Invoice Table
export const prepareFilterForInvoiceTable = (status, startDate, endDate) => {
  let filters = "";
  if (status && status?.value !== PRODUCT_TYPES_ENUMS.ALL) {
    filters = filters + `AND c.invoiceType = '${status?.value}' `;
  }

  if (startDate) {
    filters = filters + `AND c.createdAt > '${startDate}' `;
  }

  if (endDate) {
    filters = filters + `AND c.createdAt < '${endDate}' `;
  }

  return filters;
};

export const getTotalQuantityOfInvoiceBatch = (inventory, invoiceType) => {
  return inventory.reduce(
    (acc, item) =>
      acc +
      getQuantityValue(item, invoiceType === invoiceConstants.FULFILLED, true),
    0
  );
};

export const getInvStockPrice = (
  inventory,
  isDbJson = true,
  shouldMultiplyByQuantity = false
) => {
  let unitPrice = 0;
  if (isDbJson) {
    const priceObject = inventory?.price || {};
    unitPrice = priceObject?.isOnlineQuantity
      ? priceObject?.onlineUnitSellPrice || 0
      : priceObject?.unit_sell_price || 0;
  } else {
    unitPrice =
      inventory.isSeparateQuantity && inventory.isOnlineUnitSellPrice
        ? Number(inventory.onlineUnitSellPrice || 0)
        : Number(inventory.inStockPrice || 0);
  }

  if (!shouldMultiplyByQuantity) return unitPrice;

  const quantity = getQuantityValue(inventory, isDbJson, isDbJson);

  return unitPrice * quantity;
};

export const getInvoiceTotalCostOfGoods = (inventory, invoiceType) => {
  if (invoiceType === invoiceConstants.FULFILLED) {
    return inventory.reduce(
      (acc, item) => acc + getCogsValue(item, true, true, true),
      0
    );
  } else {
    return inventory.reduce(
      (acc, item) =>
        acc +
        (isStoreCustomProduct(item, "apiSource") && !item.trackQuantity
          ? Number(1 * item.costOfGoods)
          : Number(item.inStockQuantity * item.costOfGoods)),
      0
    );
  }
};

export const getBatchTotalCalculation = (
  addBatchInventory = [],
  isCountLockedItem = true
) => {
  const totals = addBatchInventory.reduce(
    (acc, item) => {
      const {
        isSeparateQuantity = false,
        isItemLocked = false,
        onlineQuantity = 0,
        inStockQuantity = 0,
        marketPrice = 0,
        costOfGoods = 0,
        onlineUnitPurchasePrice = 0,
        cashOffer = 0,
        tradeOffer = 0,
      } = item;

      // Determine quantity for totalItems / price / marketPrice
      const quantity = Number(
        isSeparateQuantity ? onlineQuantity : inStockQuantity
      );

      const shouldCountCost = isCountLockedItem ? !isItemLocked : true;

      // Aggregate totals
      acc.totalItems += getQuantityValue(item, false);

      acc.totalPrice += getInvStockPrice(item, false, true);
      acc.totalMarketPrice += marketPrice * quantity;

      if (shouldCountCost) {
        acc.totalCost +=
          (isSeparateQuantity ? onlineUnitPurchasePrice : costOfGoods) *
          quantity;
      }

      // Safely add cashOffer and tradeOffer if they exist
      acc.totalCashOffer += Number(cashOffer) * quantity;
      acc.totalTradeOffer += Number(tradeOffer) * quantity;

      return acc;
    },
    {
      totalItems: 0,
      totalPrice: 0,
      totalMarketPrice: 0,
      totalCost: 0,
      totalCashOffer: 0,
      totalTradeOffer: 0,
    }
  );

  totals.totalCost = parseToDecimalNumber(totals.totalCost);

  return totals;
};

export const getTotalStockPriceOfInvoiceBatch = (inventory, invoiceType) => {
  if (invoiceType === invoiceConstants.FULFILLED) {
    return inventory.reduce((acc, currentInv) => {
      return Number(getInvStockPrice(currentInv, true, true)) + acc;
    }, 0);
  } else {
    return inventory.reduce((acc, currentInv) => {
      return isStoreCustomProduct(currentInv, "apiSource") &&
        !currentInv.trackQuantity
        ? Number(currentInv.inStockPrice * 1)
        : Number(currentInv.inStockPrice * currentInv.inStockQuantity) + acc;
    }, 0);
  }
};

// Used in Trade, AddTradeBatch, QuickTradeIn, Add Inventory, Add Batch Inventory, Invoice
export const getAddInventoryPricePayloadObject = (
  formikValues = {},
  isEditInventory = false
) => {
  let isOnlineQuantity =
    formikValues.isOnlineQuantity || formikValues.isSeparateQuantity;
  let quantity =
    Number(formikValues.isSeparateQuantity ? 0 : formikValues.quantity) || 0; // as user can only add stock in one place at a time either online or in store hence we are setting 0 as other value
  let onlineQuantity =
    Number(formikValues.isSeparateQuantity ? formikValues.onlineQuantity : 0) ||
    0;
  const onlineUnitSellPrice =
    Number(
      formikValues.isOnlineUnitSellPrice
        ? formikValues.onlineUnitSellPrice
        : formikValues.inStockPrice
    ) || 0;
  const onlineUnitPurchasePrice =
    Number(
      formikValues?.isSeparateQuantity
        ? formikValues.onlineCostPerItem ||
            formikValues.onlineUnitPurchasePrice ||
            0
        : 0
    ) || 0;
  //if edit case then save whatever is the value
  if (isEditInventory) {
    quantity = Number(formikValues.quantity || 0);
    onlineQuantity = Number(formikValues.onlineQuantity) || 0;
  }

  return {
    set: formikValues?.set ?? EMPTY_STRING,
    type: formikValues?.type ?? EMPTY_STRING,
    grade: formikValues?.grade ?? EMPTY_STRING,
    rarity: formikValues?.rarity ?? EMPTY_STRING,
    language: formikValues?.language ?? EMPTY_STRING,
    profit: formikValues?.profit || 0,
    marketPrice: formikValues.marketPrice || 0,
    isOnlineQuantity: formikValues?.isOnlineQuantity ?? false, //isOnlineQuantity: [value of separate quantity switch (either on or off)]  isSeparateQuantity: [visiblity of seprate quantity switch]
    isSeparateQuantity:
      formikValues.isSeparateQuantity === undefined
        ? formikValues.isOnlineQuantity ?? false
        : formikValues.isSeparateQuantity ?? false,
    quantity: quantity,
    onlineQuantity: onlineQuantity,
    //-------product online and offline prices
    isOnlineUnitSellPrice: formikValues.isOnlineUnitSellPrice ?? false,
    unit_sell_price: formikValues.inStockPrice || 0,
    onlineUnitSellPrice: onlineUnitSellPrice,
    //-------product online and offline purchase prices
    unit_purchase_price: formikValues?.isSeparateQuantity
      ? 0
      : formikValues.costOfGoods || 0,
    onlineUnitPurchasePrice: onlineUnitPurchasePrice,
  };
};

// Used in AddTrade, AddTradeBatch, QuickTradeIn, AddInventory, AddBatchInventory, Invoice
export const getAddInventoryRootPayloadPartialObject = (
  priceChartingProductInfo,
  isEditInventory = false
) => {
  const valuesObject = priceChartingProductInfo?.productMetaData
    ? priceChartingProductInfo?.productMetaData
    : priceChartingProductInfo;

  return {
    subcategory:
      valuesObject?.productType === PRODUCT_TYPES_ENUMS.VIDEO_GAME
        ? getProductSubCategory(valuesObject?.genre)
        : valuesObject?.subcategory,
    productType: valuesObject.productType,
    upc: valuesObject?.upc ?? EMPTY_STRING,
    serialNumber: valuesObject?.serialNumber || EMPTY_STRING,
    cardNumber: valuesObject?.cardNumber || EMPTY_STRING,
    barcode: valuesObject?.barcode || EMPTY_STRING,
    cardRarity: valuesObject?.cardRarity || EMPTY_STRING,
    isLinkToShopify: valuesObject?.isLinkToShopify || false,
    shopifyId: valuesObject?.shopifyId || EMPTY_STRING,
    variantId: valuesObject?.variantId || EMPTY_STRING,
    shopifySettingId: valuesObject?.shopifySettingId || EMPTY_STRING,
    shopifyInventoryItemId:
      valuesObject?.shopifyInventoryItemId || EMPTY_STRING,
    imgUrl: valuesObject?.imgUrl || EMPTY_STRING,
    shopifyResetInventoryInstanceId:
      valuesObject?.shopifyResetInventoryInstanceId || EMPTY_STRING,
    fallbackImgUrl: valuesObject?.fallbackImgUrl || EMPTY_STRING,
  };
};

export const handleTransformClientInventoryJsonToDbFormat = (
  product,
  isSpreadAllProperties = false // in case we are not going to send the mapped object to the backend, we will keep all the additional properties
) => {
  let mappedObject = {
    // CAUTION SKU VALUE
    sku: [inventoryConstants.AUTO_GENERATED],
    product_id: product.productId,
    product_name: product.productName,
    category_name: product.consoleName,
    store: product.store,
    date_added: new Date(),
    price: getAddInventoryPricePayloadObject({
      ...product,
      quantity: product.inStockQuantity,
      type: product.productCondition,
    }),
    productType: product.productType,
    upc: product.upc,
    barcode: product?.barcode ?? EMPTY_STRING,
    additionalCheckList: [],
    tags: Array.isArray(product?.tags)
      ? product.tags.map((tag) =>
          tag.hasOwnProperty("label") ? tag.label : tag
        )
      : [],
    cardRarity: product?.cardRarity ?? EMPTY_STRING,
    cardNumber: "",
    apiSource: product.apiSource,
    imgUrl: product?.imgUrl,
    epid: product.epid,
    tcgPlayerUrl: product.tcgPlayerUrl,
    subcategory:
      product.apiSource === inventoryConstants.CUSTOM_ITEM
        ? product.genre
        : getProductSubCategory(product.genre, product),
    description: EMPTY_STRING,
    serialNumber: product.serialNumber,
    trackQuantity:
      product?.trackQuantity === undefined ? true : product.trackQuantity,
    isLinkToShopify: product.isLinkToShopify ?? false,
    shopifyId: `${product?.shopifyId || EMPTY_STRING}`,
    variantId: `${product?.variantId || EMPTY_STRING}`,
    shopifySettingId: `${product?.shopifySettingId}` || EMPTY_STRING,
    shopifyInventoryItemId: product?.shopifyInventoryItemId || EMPTY_STRING,
    shopifyResetInventoryInstanceId:
      product?.shopifyResetInventoryInstanceId || EMPTY_STRING,
    fallbackImgUrl: product?.fallbackImgUrl || EMPTY_STRING,
  };
  if (isSpreadAllProperties) {
    mappedObject = {
      ...product,
      ...mappedObject,
      price: {
        ...product.price,
        ...mappedObject.price,
      },
      tradeProductMetaData: {
        tradeInMarginTypeObject: product.tradeInMarginTypeObject,
      },
    };
  }
  return mappedObject;
};

export const handleTransformDbInventoryToClientJsonFormat = (
  dbItem,
  isSpreadAllProperties = false
) => {
  if (!dbItem) return null;

  // === Reverse "price" object ===
  const price = dbItem.price ?? {};

  let updatedProduct = {
    // === Core product identity ===
    store: dbItem.store,
    epid: dbItem.epid ?? EMPTY_STRING,
    skuNumber: dbItem.sku,
    upc: dbItem.upc ?? EMPTY_STRING,
    imgUrl: dbItem.imgUrl ?? EMPTY_STRING,
    apiSource: dbItem.apiSource,
    barcode: dbItem.barcode ?? EMPTY_STRING,
    productId: dbItem.product_id,
    productName: dbItem.product_name,
    variantId: dbItem.variantId || EMPTY_STRING,
    shopifyId: dbItem.shopifyId || EMPTY_STRING,
    consoleName: dbItem.category_name,
    description: dbItem.description ?? EMPTY_STRING,
    productType: dbItem.productType,
    serialNumber: dbItem.serialNumber ?? EMPTY_STRING,
    cardNumber: dbItem.cardNumber ?? EMPTY_STRING,
    cardRarity: dbItem.cardRarity ?? EMPTY_STRING,
    subcategory: dbItem.subcategory ?? EMPTY_STRING,
    isLinkToShopify: dbItem.isLinkToShopify ?? false,
    additionalCheckList: dbItem.additionalCheckList ?? [],
    genre: dbItem.subcategory, // inverse of getProductSubCategory()
    shopifySettingId: dbItem.shopifySettingId ?? EMPTY_STRING,
    shopifyInventoryItemId: dbItem.shopifyInventoryItemId ?? EMPTY_STRING,
    shopifyResetInventoryInstanceId:
      dbItem.shopifyResetInventoryInstanceId ?? EMPTY_STRING,
    fallbackImgUrl: dbItem.fallbackImgUrl ?? EMPTY_STRING,
    tcgPlayerUrl: dbItem.tcgPlayerUrl ?? EMPTY_STRING,
    trackQuantity:
      dbItem.trackQuantity === undefined ? true : dbItem.trackQuantity,
    tags: Array.isArray(dbItem.tags)
      ? dbItem.tags.map((tag) =>
          typeof tag === "string" ? { label: tag } : tag
        )
      : [],

    // === Restore all price fields from getAddInventoryPricePayloadObject ===
    set: price.set ?? "",
    type: price.type ?? "",
    grade: price.grade ?? "",
    rarity: price.rarity ?? "",
    language: price.language ?? "",
    profit: price.profit ?? 0,
    maxCash: price?.maxCash || 0,
    quantity: price.quantity ?? 0,
    maxTrade: price?.maxTrade || 0,
    productCondition: price.type ?? "",
    condition: price.type ?? "",
    marketPrice: price.marketPrice ?? 0,
    inStockQuantity: price.quantity ?? 0,
    maxBuyPrice: price?.maxBuyPrice || 0,
    inStockPrice: price.unit_sell_price ?? 0,
    onlineQuantity: price.onlineQuantity ?? 0,
    isOnlineQuantity: price?.isOnlineQuantity,
    isSeparateQuantity: price?.isSeparateQuantity, // mirrors creation logic
    totalUnitsSold: price?.totalUnitsSold || 0,
    averageBuyPrice: price?.averageBuyPrice || 0,
    averageSellPrice: price?.averageSellPrice || 0,
    isOnlineUnitSellPrice: price.isOnlineUnitSellPrice ?? false,
    onlineUnitSellPrice:
      price.onlineUnitSellPrice ??
      (price.isOnlineUnitSellPrice ? price.unit_sell_price : 0),
    costOfGoods:
      price.unit_purchase_price ?? (price.isSeparateQuantity ? 0 : 0),
    onlineCostPerItem:
      price.onlineUnitPurchasePrice ?? (price.isSeparateQuantity ? 0 : 0),
  };

  // Spread any additional properties that were originally kept
  if (isSpreadAllProperties) {
    updatedProduct = {
      ...dbItem,
      ...dbItem.price,
      ...updatedProduct,
      ...updatedProduct.price,
    };
  }

  return updatedProduct;
};

// returns true if inventory count exceeds the max limit
export const handleMaxInventoryCount = (inventoryArr, isAddOne = false) => {
  let tempCount = 0;
  if (isAddOne) {
    tempCount = 1;
  }
  return (
    inventoryArr.length + tempCount > inventoryConstants.MAX_INVENTORY_COUNT
  );
};

//-------handle submit add batch inventory
export const handleSubmitAddBatchInventory = async (
  currentStore,
  activateSpinner,
  deactivateSpinner,
  handleCloseModal,
  handleClearFilter,
  addBatchInventory,
  addBatchInventorySubmit,
  spinnerConstant = transactionConstants.ADD_TRADE_BATCH_ITEM,
  activateImportInventorySpinner = () => {},
  addBatchInventoryRetrievedDraft = {},
  fulfillInvoice = {}
) => {
  activateSpinner(spinnerConstant);
  // validations
  let isInvalid = addBatchInventory.some((inv) => {
    let quantity = getQuantityValue(inv, false);

    return quantity === 0;
  });

  if (isInvalid) {
    customToast(toastMessages.INVENTORY_INVALID_ERROR, toastType.ERROR);
    activateImportInventorySpinner({
      isVisible: false,
      message: EMPTY_STRING,
    });
    deactivateSpinner(spinnerConstant);
    return;
  } else if (handleMaxInventoryCount(addBatchInventory)) {
    customToast(inventoryConstants.MAX_INVENTORY_COUNT_ERROR, toastType.ERROR);
    deactivateSpinner(spinnerConstant);
    activateImportInventorySpinner({
      isVisible: false,
      message: EMPTY_STRING,
    });
    return;
  }

  let data = [];
  const margedBacthInventory = addBatchInventory;
  for (let index = 0; index < margedBacthInventory.length; index++) {
    const product = margedBacthInventory[index];
    product.store = {
      id: currentStore.id,
      name: currentStore.storeName,
    };
    data.push(handleTransformClientInventoryJsonToDbFormat(product));
  }

  deactivateSpinner(spinnerConstant);
  addBatchInventorySubmit(
    handleAddInventorySubmitBeautify(data),
    handleCloseModal,
    handleClearFilter,
    fulfillInvoice,
    addBatchInventoryRetrievedDraft
  );
};

//-------get condition list according to product type
export const getCustomProductConditionList = (productType, customTypes) => {
  return getTypeOrCategoryObject(customTypes, productType)?.productType ===
    PRODUCT_TYPES_ENUMS.VIDEO_GAME
    ? [
        ...inventoryConstants.VIDEO_GAME_CONDITION,
        {
          value: PRODUCT_CONDITIONS_ENUMS.USED,
          label: PRODUCT_CONDITIONS_ENUMS.USED,
        },
      ]
    : getTypeOrCategoryObject(customTypes, productType)?.productType ===
      PRODUCT_TYPES_ENUMS.TRADING_CARD
    ? [
        ...inventoryConstants.TRADING_CARD_CONDITION,
        ...inventoryConstants.OTHER_CONDITION,
      ]
    : inventoryConstants.OTHER_CONDITION;
};

export const handleIsViewTcgPlayerApiButton = (sourceApi) => {
  return sourceApi === inventoryConstants.TCG_PLAYER_API;
};

export const iShowTcgPlayerButtonLogo = (sourceApi, currentStore) => {
  return (
    sourceApi === inventoryConstants.TCG_PLAYER_API &&
    !currentStore?.isTcgPlayerButtonDisabled
  );
};

export const getInventoryInitialValueType = (modalType) => {
  let initialValueType = undefined;
  if (modalType === INVENTORY_MODAL_TYPES.ADD_FROM_NEW_TRADE_SEARCH) {
    initialValueType = INITIAL_VALUE_TYPES.ADD_INVENTORY;
  } else if (
    modalType === INVENTORY_MODAL_TYPES.EDIT_FROM_ADD_BATCH_TRADE_MODAL ||
    modalType === INVENTORY_MODAL_TYPES.EDIT_FROM_ADD_TRADE_CART
  ) {
    initialValueType = INITIAL_VALUE_TYPES.EDIT_INVENTORY_WHILE_ADDING;
  }
  return initialValueType;
};

function applyPrevFormValues(
  initialValues,
  prevFormValues = {},
  preserveMode = preserveModeConstants.ALL
) {
  const {
    serialNumber,
    quantity,
    onlineQuantity,
    inStockPrice,
    onlineUnitSellPrice,
    costOfGoods,
    cashOffer,
    tradeOffer,
  } = prevFormValues;

  const shouldKeepCoreFields =
    serialNumber !== undefined &&
    quantity !== undefined &&
    onlineQuantity !== undefined;

  if (!shouldKeepCoreFields) {
    return initialValues;
  }

  if (preserveMode === preserveModeConstants.PARTIAL) {
    return {
      ...initialValues,
      serialNumber,
      quantity,
      onlineQuantity,
    };
  }

  if (
    preserveMode === preserveModeConstants.ALL &&
    inStockPrice !== undefined &&
    onlineUnitSellPrice !== undefined &&
    costOfGoods !== undefined &&
    cashOffer !== undefined &&
    tradeOffer !== undefined
  ) {
    return {
      ...initialValues,
      serialNumber,
      quantity,
      onlineQuantity,
      inStockPrice: inStockPrice || initialValues.inStockPrice,
      onlineUnitSellPrice:
        onlineUnitSellPrice || initialValues.onlineUnitSellPrice,
      costOfGoods: costOfGoods || initialValues.costOfGoods,
      cashOffer: cashOffer || initialValues.cashOffer,
      tradeOffer: tradeOffer || initialValues.tradeOffer,
    };
  }

  return initialValues;
}
/**
 * COMPUTE INITIAL VALUES WITH PRESERVE MODE
 *
 * This is the CORE decision point for form reset vs. field preservation.
 * Called every time Formik initializes or reinitializes (condition change, tag change, etc).
 * Decides which form fields get fresh defaults and which keep user-typed values.
 *
 * THE PRESERVE MODE:
 *   Condition: if (hasInteracted && preserveMode !== NONE) { preserve values }
 *
 *   hasInteracted = false: User hasn't typed anything yet
 *     → Always return fresh defaults (stale data impossible)
 *
 *   hasInteracted = true && preserveMode = NONE: User typed, but new search triggered
 *     → Return fresh defaults (explicitly reset everything)
 *
 *   hasInteracted = true && preserveMode = PARTIAL: User typed, filter changed
 *     → Call applyPrevFormValues() → selectively preserve user input patterns:
 *         * Keep: quantity, serial number, purchase price, margins (user-typed)
 *         * Reset: market price, suggested price (computed from condition/tags)
 *
 *   hasInteracted = true && preserveMode = ALL: User typed, submit/Shopify operation
 *     → Call applyPrevFormValues(mode=ALL) → preserve everything unchanged
 *
 * This prevents stale data carryover while preserving user effort:
 *   1. Fresh modals never carry old data (hasInteracted=false always uses defaults)
 *   2. Active workflows preserve user typing (hasInteracted=true keeps values)
 *
 * Called From:
 *   - AddInventoryItem.jsx initial values spread
 *   - AddTrade.jsx initial values spread
 *   - BatchProductEdit.jsx initial values spread
 *   - Edit inventory flows
 */
export const getAddInventoryPartialInitialValues = (
  productDataObject,
  initialValueType = INITIAL_VALUE_TYPES.ADD_INVENTORY,
  formikRef,
  preserveModeState = {
    mode: preserveModeConstants.NONE,
    hasInteracted: false,
  }
) => {
  const { mode: preserveMode, hasInteracted } = preserveModeState;
  // case add inventory, add trade
  let initialValues = {
    serialNumber: productDataObject?.serialNumber ?? EMPTY_STRING,
    // never prioritize ..price.property over .property as it will prioritize the saved value over new calculated value (i.e condition change after save in EDIT_FROM_ADD_BATCH_TRADE_MODAL)
    grade:
      productDataObject?.grade ||
      productDataObject?.price?.grade || // edit inventory case
      TCG_PLAYER_API_GRADE_ENUMS.UNGRADED, //default selected graidng,
    language:
      productDataObject?.language ||
      productDataObject?.price?.language ||
      EMPTY_STRING,
    set:
      productDataObject?.set || productDataObject?.price?.set || EMPTY_STRING,
    cardRarity: productDataObject?.cardRarity || EMPTY_STRING,
    rarity:
      productDataObject?.rarity ||
      productDataObject?.price?.rarity ||
      EMPTY_STRING,
    barcode: productDataObject?.barcode || EMPTY_STRING,
    onlineUnitSellPrice:
      productDataObject?.onlineUnitSellPrice ||
      productDataObject?.price?.onlineUnitSellPrice ||
      Number(productDataObject?.inStockPrice || 0) ||
      0,
    inStockPrice:
      productDataObject?.inStockPrice ||
      productDataObject?.price?.unit_sell_price ||
      0,
    quantity: 1,
    onlineQuantity: 1,

    // for the first time when modal opens we get total online stock in onlineQuantity, but afterwards we move it to onlineInStockQuantity and keep the quantity to add in onlineQuantity as backend expects the increase in it onlineQuantity
    // inStockQuantity:
    //   productDataObject?.inStockQuantity ?? productDataObject?.price?.quantity,
    onlineInStockQuantity:
      productDataObject?.onlineInStockQuantity ??
      productDataObject?.onlineQuantity,
    isSeparateQuantity: productDataObject?.isSeparateQuantity ?? false,
    isOnlineQuantity: productDataObject?.isOnlineQuantity ?? false,
    isOnlineUnitSellPrice:
      true ||
      productDataObject?.isOnlineUnitSellPrice ||
      productDataObject?.price?.isOnlineUnitSellPrice ||
      false,
    isLinkToShopify: productDataObject.isLinkToShopify ?? false,
    onlineUnitPurchasePrice:
      productDataObject?.onlineUnitPurchasePrice ||
      productDataObject?.costOfGoods ||
      0,
    cashOffer: productDataObject?.cashOffer || EMPTY_STRING,
    tradeOffer: productDataObject?.tradeOffer || EMPTY_STRING,
    onlineCashOffer:
      productDataObject?.onlineCashOffer ||
      productDataObject?.cashOffer ||
      EMPTY_STRING,
    onlineTradeOffer:
      productDataObject?.onlineTradeOffer ||
      productDataObject?.tradeOffer ||
      EMPTY_STRING,
    variantId: productDataObject?.variantId || EMPTY_STRING,
    shopifyId: productDataObject?.shopifyId || EMPTY_STRING,
    shopifyInventoryItemId:
      productDataObject?.shopifyInventoryItemId || EMPTY_STRING,
    shopifyResetInventoryInstanceId:
      productDataObject?.shopifyResetInventoryInstanceId || EMPTY_STRING,
    fallbackImgUrl: productDataObject?.fallbackImgUrl,
  };
  // case: edit inventory screen
  if (initialValueType === INITIAL_VALUE_TYPES.EDIT_INVENTORY) {
    initialValues.quantity =
      Number(productDataObject?.price?.quantity) ??
      Number(productDataObject?.quantity) ??
      0;
    initialValues.onlineQuantity =
      Number(productDataObject?.price?.onlineQuantity) ??
      Number(productDataObject?.onlineQuantity) ??
      0;
  }
  // case: batch tradde edit, trade cart edit, batch inventory edit
  else if (
    initialValueType === INITIAL_VALUE_TYPES.EDIT_INVENTORY_WHILE_ADDING
  ) {
    initialValues.quantity =
      Number(productDataObject?.price?.quantity) ||
      Number(productDataObject?.quantity) ||
      1;
    initialValues.onlineQuantity =
      Number(productDataObject?.price?.onlineQuantity) ||
      Number(productDataObject?.onlineQuantity) ||
      1;
  }
  // Preserve only after user interaction, and only when mode allows it.
  if (hasInteracted && preserveMode !== preserveModeConstants.NONE) {
    // Reuse previous values according to preserve mode (PARTIAL or ALL).
    initialValues = applyPrevFormValues(
      initialValues,
      formikRef?.current?.values,
      preserveMode
    );
  }
  return initialValues;
};

export const getFormattedCondition = (priceChartingProductInfo) => {
  const productMetaData = priceChartingProductInfo?.productMetaData
    ? priceChartingProductInfo?.productMetaData
    : priceChartingProductInfo;
  let grade = productMetaData?.grade || productMetaData?.price?.grade;
  let condition =
    productMetaData?.price?.type ||
    productMetaData?.type ||
    productMetaData?.condition?.value ||
    productMetaData?.productCondition?.value ||
    productMetaData?.condition ||
    productMetaData?.productCondition;

  return productMetaData?.productType === PRODUCT_TYPES_ENUMS.TRADING_CARD &&
    grade &&
    grade !== TCG_PLAYER_API_GRADE_ENUMS.UNGRADED
    ? `${String(grade).replaceAll("Graded", EMPTY_STRING)} ${condition}`
    : condition;
};

export const handleBeautifyMarketPrice = (
  productDataObject,
  isThousandSeparate = false,
  isConvertToUsd = false
) => {
  let productMetaData = productDataObject?.productMetaData
    ? productDataObject?.productMetaData
    : productDataObject;
  let tempMarketPrice = 0;
  tempMarketPrice =
    productMetaData?.apiSource === inventoryConstants.TCG_PLAYER_API ||
    productMetaData?.sourceApi === inventoryConstants.TCG_PLAYER_API ||
    isConvertToUsd
      ? productMetaData?.marketPrice ?? 0
      : parseToDecimalNumber(convertToUsd(productMetaData?.marketPrice ?? 0));
  tempMarketPrice = Number(tempMarketPrice) || 0;

  if (isThousandSeparate) {
    tempMarketPrice = parseToThousandSeperatorDecimalNumber(tempMarketPrice);
  }
  return tempMarketPrice || 0;
};

export const getCustomProductIfExist = (
  values,
  customTypes,
  customCategories,
  editCustomProduct,
  customSubCategories,
  customProductSelectedTags,
  isCustomTrade = false
) => {
  const findProduct = editCustomProduct?.productsAvailableInInventory?.find(
    (product) => {
      if (
        product.product_name === values.product_name &&
        product.price.type === values.condition &&
        product.productType ===
          getTypeOrCategoryObject(
            customTypes,
            isCustomTrade ? values.productType : values.type
          )?.productType &&
        product.category_name ===
          getTypeOrCategoryObject(
            customCategories,
            isCustomTrade ? values.category_name : values.category
          )?.name &&
        (!values.subCategory ||
          product.subcategory ===
            getTypeOrCategoryObject(
              customSubCategories,
              isCustomTrade ? values.subcategory : values.subCategory
            )?.name) &&
        isTagsEqual(product.tags, [
          ...customProductSelectedTags,
          inventoryConstants.CUSTOM_ITEM_TAG,
        ])
      ) {
        return product;
      }
    }
  );
  return findProduct;
};

export const getCustomItemInitialValues = (
  isProductEdit,
  productToEdit,
  customTypes,
  customCategories,
  customSubCategories,
  isEditCustomItemModal
) => {
  return {
    quantity:
      isProductEdit && isEditCustomItemModal
        ? productToEdit?.price?.quantity
        : 1,
    inStockPrice: isProductEdit
      ? productToEdit?.price?.unit_sell_price || 0
      : 0,
    costOfGoods: 0,
    trackQuantity: isProductEdit
      ? productToEdit?.trackQuantity === undefined
        ? true
        : productToEdit.trackQuantity
      : true,
    upc: isProductEdit ? productToEdit?.upc || EMPTY_STRING : EMPTY_STRING,
    productType: isProductEdit
      ? getTypeOrCategoryIdByName(customTypes, productToEdit?.productType) ||
        EMPTY_STRING
      : EMPTY_STRING,
    product_name: isProductEdit
      ? productToEdit?.product_name || EMPTY_STRING
      : EMPTY_STRING,
    condition: isProductEdit
      ? productToEdit?.price?.type || EMPTY_STRING
      : EMPTY_STRING,
    category_name: isProductEdit
      ? getTypeOrCategoryIdByName(
          customCategories,
          productToEdit.category_name,
          productToEdit?.productType
        ) || EMPTY_STRING
      : EMPTY_STRING,
    subcategory: isProductEdit
      ? getTypeOrCategoryIdByName(
          customSubCategories,
          productToEdit.subcategory,
          productToEdit?.productType,
          productToEdit.category_name
        ) || EMPTY_STRING
      : EMPTY_STRING,
    description: isProductEdit
      ? productToEdit?.description || EMPTY_STRING
      : EMPTY_STRING,
    isLinkToShopify: isProductEdit
      ? productToEdit?.isLinkToShopify || false
      : false,
    isOnlineUnitSellPrice: false,
    isOnlineQuantity: isProductEdit
      ? productToEdit?.isOnlineQuantity || false
      : false,
    onlineQuantity: isProductEdit ? productToEdit?.onlineQuantity || 1 : 1,
    onlineUnitSellPrice: isProductEdit
      ? productToEdit?.onlineUnitSellPrice ||
        productToEdit?.price?.onlineUnitSellPrice ||
        0
      : 0,
    shopifyId: isProductEdit
      ? productToEdit?.shopifyId || EMPTY_STRING
      : EMPTY_STRING,
    shopifySettingId: isProductEdit
      ? productToEdit?.shopifySettingId || EMPTY_STRING
      : EMPTY_STRING,
    variantId: isProductEdit
      ? productToEdit?.variantId || EMPTY_STRING
      : EMPTY_STRING,
    isSeparateQuantity: isProductEdit
      ? productToEdit?.isSeparateQuantity || false
      : false,
  };
};

export const getBatchCustomItemSubmitMapping = (
  values,
  customTypes,
  customCategories,
  customSubCategories,
  customProductSelectedTags,
  editCustomProduct
) => {
  return {
    productId: inventoryConstants.STORE_CUSTOM_PRODUCT,
    subcategory:
      getTypeOrCategoryObject(customSubCategories, values.subcategory)?.name ||
      "",
    genre:
      getTypeOrCategoryObject(customSubCategories, values.subcategory)?.name ||
      "",
    upc: values.upc ?? EMPTY_STRING,
    tags: [
      ...(customProductSelectedTags || []),
      inventoryConstants.CUSTOM_ITEM_TAG,
    ],
    marketPrice: values.inStockPrice,
    description: values.description,
    inStorePrice: 0,
    inStockPrice: values.inStockPrice,
    inStockQuantity: values.quantity,
    isItemLocked: false,
    isDataFetching: false,
    productsAvailableInInventory: editCustomProduct
      ? editCustomProduct?.productsAvailableInInventory
      : [],
    consoleName: getTypeOrCategoryObject(customCategories, values.category_name)
      ?.name,
    productName: values.product_name,
    productType: getTypeOrCategoryObject(customTypes, values.productType)
      ?.productType,
    productCondition: values.condition,
    apiSource: inventoryConstants.STORE_CUSTOM_PRODUCT,
    trackQuantity: values.trackQuantity,
    productConditionList:
      getCustomProductConditionList(values.productType, customTypes) || [],
    tcgPlayerUrl: EMPTY_STRING,
    serialNumber: EMPTY_STRING,
    isAddedFromCreateCustomItemModal: true,

    isLinkToShopify: values.isLinkToShopify || false,
    isOnlineQuantity: values.isOnlineQuantity || false,
    isSeparateQuantity: values.isSeparateQuantity || false,
    isOnlineUnitSellPrice: values.isOnlineUnitSellPrice || false,
    onlineQuantity: values.onlineQuantity || 1,
    onlineUnitSellPrice: values.onlineUnitSellPrice || 0,
    shopifyId: values.shopifyId || EMPTY_STRING,
    shopifySettingId: values.shopifySettingId || EMPTY_STRING,
    shopifySettingsObject: values.shopifySettingsObject || null,
    variantId: values.variantId || EMPTY_STRING,
    shopifyResetInventoryInstanceId:
      values?.shopifyResetInventoryInstanceId || EMPTY_STRING,
    fallbackImgUrl: values?.fallbackImgUrl,
  };
};
export const getBatchCustomItemInitialValues = (
  editCustomProduct,
  customTypes,
  customCategories,
  customSubCategories,
  shopifySettings
) => {
  return {
    quantity: editCustomProduct.inStockQuantity || 1,
    inStockPrice: editCustomProduct?.inStockPrice || 0,
    costOfGoods: editCustomProduct?.costOfGoods || 0,
    upc: editCustomProduct?.upc || EMPTY_STRING,
    productType: editCustomProduct
      ? getTypeOrCategoryIdByName(customTypes, editCustomProduct?.productType)
      : EMPTY_STRING,
    trackQuantity:
      editCustomProduct?.trackQuantity === undefined
        ? true
        : editCustomProduct?.trackQuantity,
    product_name: editCustomProduct?.productName || EMPTY_STRING,
    category_name: editCustomProduct
      ? getTypeOrCategoryIdByName(
          customCategories,
          editCustomProduct.consoleName,
          editCustomProduct?.productType
        )
      : EMPTY_STRING,
    condition: editCustomProduct?.productCondition || EMPTY_STRING,
    subcategory: editCustomProduct
      ? getTypeOrCategoryIdByName(
          customSubCategories,
          editCustomProduct.genre,
          editCustomProduct?.productType,
          editCustomProduct.consoleName
        )
      : EMPTY_STRING,
    description: editCustomProduct?.description || EMPTY_STRING,
    isLinkToShopify:
      editCustomProduct?.isLinkToShopify ??
      shopifySettings?.isLinkToShopify ??
      false,
    isOnlineUnitSellPrice:
      editCustomProduct?.isOnlineUnitSellPrice ??
      shopifySettings?.isOnlineUnitSellPrice ??
      false,
    isOnlineQuantity:
      editCustomProduct?.isOnlineQuantity ??
      shopifySettings?.isOnlineQuantity ??
      false,
    isSeparateQuantity: editCustomProduct?.isSeparateQuantity || false,
    onlineQuantity: editCustomProduct?.onlineQuantity || 1,
    onlineUnitSellPrice: editCustomProduct?.onlineUnitSellPrice || 0,
    shopifyId: editCustomProduct?.shopifyId || "",
    shopifySettingId:
      editCustomProduct?.shopifySettingId || shopifySettings?.id || "",
    shopifySettingsObject: editCustomProduct?.shopifySettingsObject || null,
    variantId: editCustomProduct?.variantId || "",
  };
};

export const isShopifyTransaction = (id) => {
  return (
    typeof id === "string" && id.toLowerCase().includes(globalConstants.SHOPIFY)
  );
};

export const isShopifyOrderHistory = (row) => {
  return (
    row?.outlet?.name?.trim().replace(/\s+/g, "").toLowerCase() ===
    globalConstants.SHOPIFY.toLowerCase().replace(/\s+/g, "")
  );
};

export const isTransferInventoryEnabled = (currentStoreCompany, stores) => {
  if (!currentStoreCompany || currentStoreCompany.isDummyCompany) return false;
  const companyId = currentStoreCompany.id;
  const storeCount = stores.filter(
    (store) => store?.companyId === companyId
  ).length;
  return storeCount > 1;
};
const roundNumberFunc = (number) => {
  return Number(Math.round(Number(number) * 1e2) / 1e2) || 0;
};
export const handleAverageOutUnitPurcasePrice = (
  dbProductQuantity,
  newProductQuantity,
  dbProductUnitPurchasePrice,
  newProductUnitPurchasePrice
) => {
  return Number(dbProductQuantity) <= 0
    ? Number(newProductUnitPurchasePrice)
    : dbProductUnitPurchasePrice === newProductUnitPurchasePrice
    ? Number(dbProductUnitPurchasePrice)
    : roundNumberFunc(
        (Number(dbProductQuantity) * Number(dbProductUnitPurchasePrice) +
          Number(newProductQuantity) * Number(newProductUnitPurchasePrice)) /
          (Number(newProductQuantity) + Number(dbProductQuantity))
      );
};

export const getUserNameOnReceipt = (newTradeData, user) => {
  const getUserFromProcessedBy = (processedBy) => {
    let firstName = String(processedBy).split(" ")[0];
    let lastName = String(processedBy).split(" ");
    lastName.shift();
    lastName = lastName.join(" ");
    return {
      firstName: firstName,
      lastName: lastName,
    };
  };
  return newTradeData?.processedBy
    ? getUserFromProcessedBy(newTradeData?.processedBy)
    : user;
};

// Map that gets called right before submitting the add inventory request to the backend api (add trade, add inventory, batch inventory etc). This is to add over the top validation and each flow specific mapping should be handled inside the each flow's submit handler, this only handles additional checks which will be same in all types of submit (add inventory, add trade, add batch) to add extra checks (as UI behaviour is unpredictable) and handle validation or null values error in add inventory submission from add trade (draft, submit, split), add batch inventory, add inventory
export const handleAddInventorySubmitBeautify = (inventory) => {
  let cleanInventory = null;
  const handleApplyExtraChecksOnInventory = (inv) => {
    // handle onlineUnitSellPrice
    if (inv.price.isOnlineUnitSellPrice) {
      if (handleIsInvalidInteger(inv.price.onlineUnitSellPrice)) {
        inv.price.onlineUnitSellPrice = inv.price.unit_sell_price || 0;
      }
    } else {
      inv.price.onlineUnitSellPrice = 0;
    }
    // handle random error of tag[x] must be a string (ideally it should not happen, and tags in each product must have a valid value but to make sure trade gets processed)
    if (!Array.isArray(inv.tags)) {
      inv.tags = [];
    } else {
      inv.tags = inv.tags.filter((tag) => tag).map((tag) => String(tag));
    }
    // if shopifyId === EMPTY_STRING, then make sure variantId, shopifyInventoryItemId also EMPTY_STRING as this is not possible for inventory to have variantId and no shopifyId
    if (inv.shopifyId === EMPTY_STRING) {
      inv.shopifyId = EMPTY_STRING;
      inv.variantId = EMPTY_STRING;
      inv.shopifyInventoryItemId = EMPTY_STRING;
    }
    if (inv.shopifyResetInventoryInstanceId === undefined) {
      inv.shopifyResetInventoryInstanceId = EMPTY_STRING;
    }
    // In shopify syncing for some reason img becomes null and if img is null or undefined then make sure it is EMPTY_STRING as backend expects it to be string, we don't want to block the add trade because of one existing product having null imgUrl, so we will just make it EMPTY_STRING and let the trade go through
    if (inv.imgUrl === null || inv.imgUrl === undefined) {
      inv.imgUrl = EMPTY_STRING;
    }
    if (!inv.fallbackImgUrl) {
      inv.fallbackImgUrl = inv.imgUrl;
    }
  };

  // if inventory is array then apply on all inventory objects and return array
  if (Array.isArray(inventory)) {
    cleanInventory = inventory.map((invObj) => {
      handleApplyExtraChecksOnInventory(invObj);
      return invObj;
    });
  }
  // if inventory is not array instead it is an object then apply on the object and return the object
  else if (
    !Array.isArray(inventory) &&
    typeof inventory === "object" &&
    inventory.hasOwnProperty("price") &&
    typeof inventory.price === "object" // make sure it is the add inventory payload
  ) {
    handleApplyExtraChecksOnInventory(inventory);
    cleanInventory = inventory;
  }
  return cleanInventory;
};

export const handleFocusRefInput = (elementRef, isFocus = true) => {
  if (isFocus) {
    elementRef.current?.focus();
  } else {
    elementRef.current?.blur();
  }
};

export const getProfitMargin = (sellPrice, purchasePrice) => {
  let profitMargin =
    sellPrice !== null && sellPrice !== undefined
      ? convertToFixedPrecision(((sellPrice - purchasePrice) / sellPrice) * 100)
      : 0;
  if (
    profitMargin === Infinity ||
    profitMargin === -Infinity ||
    isNaN(profitMargin)
  ) {
    profitMargin = 0;
  }
  return profitMargin;
};

export const getProfit = (sellPrice, purchasePrice) => {
  return sellPrice !== null && sellPrice !== undefined
    ? convertToFixedPrecision(sellPrice - purchasePrice)
    : 0;
};

export const calculateProfitAndMargin = (cost, price) => {
  const profit = getProfit(price, cost);
  const margin = getProfitMargin(price, cost);

  return { profit, margin };
};

// if seprate quantity then unitPurchasePrice will also be separate. otherwise same unitPurchasePrice
export const getUnitPurchasePriceOfDbProduct = (
  product,
  productJsonType = PRODUCT_JSON_TYPE.DB_PRODUCT,
  isEditingOnlinePricing
) => {
  let unitPurchasePrice = 0;
  if (productJsonType === PRODUCT_JSON_TYPE.DB_PRODUCT) {
    if (product?.price?.isOnlineQuantity) {
      unitPurchasePrice = product.price.onlineUnitPurchasePrice ?? 0;
    } else {
      unitPurchasePrice = product?.price?.unit_purchase_price ?? 0;
    }
  } else if (productJsonType === PRODUCT_JSON_TYPE.EDIT_INVENTORY) {
    if (isEditingOnlinePricing && product.isOnlineQuantity) {
      unitPurchasePrice =
        product?.onlineCostPerItem ?? product.onlineUnitPurchasePrice ?? 0;
    } else {
      unitPurchasePrice = product?.costPerItem ?? 0;
    }
  }
  return Number(unitPurchasePrice || 0);
};

export const getTotalQuantityOfTransaction = (trans) => {
  const inventory = Array.isArray(trans?.inventory)
    ? trans.inventory
    : Array.isArray(trans)
    ? trans
    : [];

  let sumQuantity = 0;
  inventory.forEach((inv) => {
    let tempQuantity = 0;
    // case: inventory batch draft
    if (inv.hasOwnProperty("inStockQuantity")) {
      tempQuantity = inv.isOnlineQuantity
        ? inv.onlineQuantity
        : inv.inStockQuantity;
    }
    // case: trade transaction draft
    else if (
      inv.hasOwnProperty("price") &&
      typeof inv.price === "object" &&
      inv.price.hasOwnProperty("quantity")
    ) {
      tempQuantity = getQuantityValue(inv, true, true);
    }
    // case: sale transaction draft
    else if (inv.hasOwnProperty("quantity")) {
      tempQuantity = inv.isOnlineQuantity ? inv.onlineQuantity : inv.quantity;
    }

    sumQuantity = sumQuantity + Number(tempQuantity || 0);
  });

  return sumQuantity;
};

export const getInventoryHistoryType = (trans) => {
  const transactionType = trans?.TransactionType;
  const type = `${transactionType} ${
    trans?.isAddShrinkTracking
      ? "(Lost/Stolen)"
      : trans?.isDefectiveReturn
      ? "(Defective)"
      : EMPTY_STRING
  }`;

  return type;
};

export const handleDownloadImportInventoryResponseFile = async (url) => {
  const response = await fetch(url);
  const json = await response.json();
  const frontEndInvalidRows = [];
  const backEndInvalidRows = [];
  // Invalid items (it can be only front-end invalid (failed initial validation), only backend invalid (failed during addition: duplicate product, not found on pricecharting etc) or front-end invalid + backend invalid)
  json.forEach((row) => {
    if (row.hasOwnProperty("Product Name")) {
      frontEndInvalidRows.push(row);
    } else {
      backEndInvalidRows.push(row);
    }
  });
  let temp = {};
  downloadCSV(
    [
      ...frontEndInvalidRows.map((inv) => {
        temp = {
          ...inv,
          "Rejection Reason": beautifyStringForImport(inv.errorMessage),
        };
        delete temp.errorMessage;
        return temp;
      }),
      ...backEndInvalidRows.map((inv) => ({
        "Product Name": beautifyStringForImport(inv.product_name),
        "Product Type": inv.productType,
        Category: inv.category_name,
        Subcategory: inv.subcategory,
        Condition: inv.price.type,
        "Serial Number": inv.serialNumber,
        Quantity: inv.price.quantity,
        "Cost of Goods": inv.price.unit_purchase_price,
        "Sell Price": inv.price.unit_sell_price,
        SKU: exportStringArrayFunc(inv.sku),
        UPC: exportUpcFunc(inv.upc),
        Tags: exportTagFunc(inv.tags),
        "Rejection Reason": beautifyStringForImport(inv.errorMessage),
      })),
    ],
    "invalid inventory products"
  );
};

export const isAddInventoryInProgress = (spinnerArray) => {
  return isSpinnerEnabled(spinnerArray, [
    inventoryConstants.ADD_INVENTORY,
    transactionConstants.ADD_TRADE_BATCH_ITEM,
  ]);
};

export const handleEditCardClick = ({
  productDetail,
  setEditCustomProduct,
  toggleCustomItemModal,
  setEditTradeObject,
  setPriceChartingProductInfo,
  toggleAddInventoryItemModal,
}) => {
  if (productDetail?.apiSource === inventoryConstants.STORE_CUSTOM_PRODUCT) {
    setEditCustomProduct(productDetail);
    toggleCustomItemModal();
  } else {
    setEditTradeObject(productDetail);
    toggleAddInventoryItemModal();
    setPriceChartingProductInfo({
      productMetaData: productDetail,
      productObject: productDetail.productObject,
    });
  }
};

// #Custom is an internal tag — exclude it from UI counts and display lists.
// if passed title is array of tags then filter the tag to not show the custom item tag (this is useful when rendering ProductTagColumn)
export const getDisplayProductTags = (tags = []) => {
  if (!tags?.length) {
    return [];
  }

  return tags.filter((tag) => tag !== inventoryConstants.CUSTOM_ITEM_TAG);
};

export const getDisplayProductTagCount = (tags = []) => {
  return getDisplayProductTags(tags).length;
};

export const getProductImage = (product) => {
  if (product?.imgUrl) {
    return product.imgUrl;
  }
  if (product?.fallbackImgUrl) {
    return product.fallbackImgUrl;
  }
  return ItemImage;
};

//------ check product serial number is unique
export const isSerialNumberUnique = (
  serialNumber,
  products,
  isDbJson = true
) => {
  //------ Check if the serial number already exists in the products array
  console.log("isSerialNumberUnique", serialNumber, products);
  return !products.some(
    (product) =>
      product?.serialNumber &&
      serialNumber &&
      product.serialNumber === serialNumber &&
      getQuantityValue(product, isDbJson, true) > 0
  );
};
